import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { FileUpload } from 'primereact/fileupload';
import { Panel } from 'primereact/panel';
import { Card } from 'primereact/card';
import { Badge } from 'primereact/badge';
import { Toast } from 'primereact/toast';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Toolbar } from 'primereact/toolbar';
import { SplitButton } from 'primereact/splitbutton';
import { Checkbox } from 'primereact/checkbox';
import { Row, Col } from 'react-bootstrap';
import TemplatePreview from './TemplatePreview';
import TemplateEditor from './TemplateEditor';
import TemplateComparison from './TemplateComparison';
import CarteV2Service from '../../../../services/CarteV2Service';

class TemplateManager extends Component {
    constructor(props) {
        super(props);
        this.state = {
            templates: [],
            countries: [],
            selectedTemplate: null,
            selectedTemplates: [],
            globalFilter: null,
            filters: {
                country: null,
                type: null,
                category: null,
                status: null
            },
            showDialog: false,
            showPreviewDialog: false,
            showComparisonDialog: false,
            showEditorDialog: false,
            loading: false,
            dialogMode: 'create', // create, edit, duplicate
            templateForm: {
                name: '',
                country: '',
                isActive: true,
                isDefault: false
            }
        };
        this.toast = React.createRef();
    }
    loadCountries = async () => {
        const { t } = this.props;
        this.setState({ countriesLoading: true });
        try {
            const data = await CarteV2Service.getCountries();
            const countriesData = Array.isArray(data) ? data : [];
            this.setState({ countries: countriesData });
            console.log('Pays chargés:', countriesData.length);
        } catch (error) {
            console.error('Erreur chargement pays:', error);
            this.showToast('error', 'Erreur', 'Erreur lors du chargement des pays');
            this.setState({ countries: [] });
        } finally {
            this.setState({ countriesLoading: false });
        }
    };
    getCountryOptions = () => {
        return this.state.countries.map(country => ({
            label: country.name || country.Name,
            value: country.code || country.Code
        }));
    };

    getTypes = () => {
        const { t } = this.props;
        return [
            { label: t('templateManager.types.standard'), value: 'STANDARD' },
            { label: t('templateManager.types.professional'), value: 'PROFESSIONAL' },
            { label: t('templateManager.types.family'), value: 'FAMILY' },
            { label: t('templateManager.types.vip'), value: 'VIP' }
        ];
    };

    getCategories = () => {
        const { t } = this.props;
        return [
            { label: t('templateManager.categories.health'), value: 'HEALTH' },
            { label: t('templateManager.categories.insurance'), value: 'INSURANCE' }
        ];
    };

    getStatusOptions = () => {
        const { t } = this.props;
        return [
            { label: t('templateManager.status.active'), value: true },
            { label: t('templateManager.status.inactive'), value: false }
        ];
    };

    componentDidMount() {
        this.loadCountries().then(() => {
            this.loadTemplates();
        });
    }

    loadTemplates = async () => {
        const { t } = this.props;
        this.setState({ loading: true });
        try {
            // Utilisation du service API réel
            const templates = await CarteV2Service.getTemplates(this.state.filters);

            // Adapter les données API au format attendu par le composant
            const adaptedTemplates = templates.map(template => ({
                id: template.id,
                name: template.templateName || template.name || `Template ${template.id}`,
                description: template.description,
                country: template.countryCode,
                type: template.cardType,
                category: template.category,
                isActive: template.isActive,
                isDefault: template.isDefault,
                version: template.version || '1.0',
                createdAt: new Date(template.createdAt),
                updatedAt: new Date(template.updatedAt),
                usageCount: template.usageCount || 0,
                frontDesign: template.frontDesignUrl,
                backDesign: template.backDesignUrl,
                variables: template.variables || [],
                metadata: template.metadata || {}
            }));

            // Sort by creation date (descending - newest first)
            adaptedTemplates.sort((a, b) => b.createdAt - a.createdAt);

            this.setState({ templates: adaptedTemplates });
        } catch (error) {
            console.error('Erreur lors du chargement des templates:', error);
            this.showToast('error', 'Erreur', t('templateManager.messages.loadError'));
        } finally {
            this.setState({ loading: false });
        }
    };

    showToast = (severity, summary, detail) => {
        this.toast.current.show({ severity, summary, detail });
    };

    openDialog = (mode, template = null) => {
        const { t } = this.props;
        if (mode === 'edit' && template) {
            this.setState({
                dialogMode: mode,
                templateForm: { ...template },
                showDialog: true
            });
        } else if (mode === 'duplicate' && template) {
            this.setState({
                dialogMode: mode,
                templateForm: {
                    ...template,
                    id: null,
                    name: `${template.name} ${t('templateManager.messages.copy')}`,
                    isDefault: false
                },
                showDialog: true
            });
        } else {
            this.setState({
                dialogMode: 'create',
                templateForm: {
                    name: '',
                    country: '',
                    isActive: true,
                    isDefault: false
                },
                showDialog: true
            });
        }
    };

    saveTemplate = async () => {
        const { t } = this.props;
        try {
            const { dialogMode, templateForm } = this.state;

            // Validation - Nom et pays obligatoires
            if (!templateForm.name || !templateForm.country) {
                this.showToast('warn', 'Attention', t('templateManager.messages.fillRequired'));
                return;
            }

            this.setState({ loading: true });

            // Préparer les données pour l'API - Seulement nom et pays
            const apiData = {
                templateName: templateForm.name,
                countryCode: templateForm.country,
                isActive: templateForm.isActive,
                isDefault: templateForm.isDefault
            };

            if (dialogMode === 'create' || dialogMode === 'duplicate') {
                // Créer nouveau template via API
                const createdTemplate = await CarteV2Service.createTemplate(apiData);
                this.showToast('success', 'Succès', t('templateManager.messages.createSuccess'));
            } else {
                // Modifier template existant via API
                await CarteV2Service.updateTemplate(templateForm.id, apiData);
                this.showToast('success', 'Succès', t('templateManager.messages.updateSuccess'));
            }

            // Recharger la liste des templates
            await this.loadTemplates();
            this.setState({ showDialog: false });
        } catch (error) {
            console.error('Erreur lors de la sauvegarde:', error);
            this.showToast('error', 'Erreur', error.message || t('templateManager.messages.saveError'));
        } finally {
            this.setState({ loading: false });
        }
    };

    deleteTemplate = async (template) => {
        const { t } = this.props;
        try {
            await CarteV2Service.deleteTemplate(template.id);
            await this.loadTemplates(); // Recharger la liste
            this.showToast('success', 'Succès', t('templateManager.messages.deleteSuccess'));
        } catch (error) {
            console.error('Erreur lors de la suppression:', error);
            this.showToast('error', 'Erreur', error.message || t('templateManager.messages.deleteError'));
        }
    };

    exportTemplate = (template) => {
        const { t } = this.props;
        if (!template) {
            this.showToast('error', 'Erreur', t('templateManager.messages.exportError'));
            return;
        }

        const dataStr = JSON.stringify(template, null, 2);
        const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);

        // Handle both name and TemplateName
        const templateName = template.name || template.TemplateName || 'template';
        const exportFileDefaultName = `template_${templateName.replace(/\s+/g, '_')}_${Date.now()}.json`;

        const linkElement = document.createElement('a');
        linkElement.setAttribute('href', dataUri);
        linkElement.setAttribute('download', exportFileDefaultName);
        linkElement.click();

        this.showToast('success', 'Export', t('templateManager.messages.exportSuccess'));
    };

    importTemplate = (event) => {
        const { t } = this.props;
        const file = event.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const imported = JSON.parse(e.target.result);
                    const newTemplate = {
                        ...imported,
                        id: Date.now(),
                        name: `${imported.name} ${t('templateManager.messages.imported')}`,
                        createdAt: new Date(),
                        updatedAt: new Date()
                    };
                    this.setState(prevState => ({
                        templates: [...prevState.templates, newTemplate]
                    }));
                    this.showToast('success', 'Import', t('templateManager.messages.importSuccess'));
                } catch (error) {
                    this.showToast('error', 'Erreur', t('templateManager.messages.importError'));
                }
            };
            reader.readAsText(file);
        }
    };

    // Navigation vers l'éditeur de template en pleine page
    openTemplateEditor = (template = null) => {
        // S'assurer que le composant est monté avant la navigation
        if (this.props.history) {
            if (template && template.id) {
                // Navigate with template ID in URL
                this.props.history.push(`/cartes-v2/templates/${template.id}/editor`);
            } else {
                // New template - no ID in URL
                this.props.history.push('/cartes-v2/templates/editor');
            }
        } else {
            console.error('Router history is not available');
            this.showToast('error', 'Erreur', 'Impossible de naviguer vers l\'éditeur');
        }
    };

    // Colonnes du DataTable
    nameBodyTemplate = (rowData) => {
        return (
            <div>
                <div className="font-bold">{rowData.name}</div>
                <div className="text-sm text-muted">v{rowData.version}</div>
            </div>
        );
    };

    countryBodyTemplate = (rowData) => {
        const country = this.state.countries.find(c => 
            (c.code || c.Code) === rowData.country
        );
        return country ? (country.name || country.Name) : rowData.country;
    };

    typeBodyTemplate = (rowData) => {
        const type = this.getTypes().find(t => t.value === rowData.type);
        return type ? type.label : rowData.type;
    };

    statusBodyTemplate = (rowData) => {
        const { t } = this.props;
        return (
            <div className="flex align-items-center gap-2">
                <Badge
                    value={rowData.isActive ? t('templateManager.status.active') : t('templateManager.status.inactive')}
                    severity={rowData.isActive ? 'success' : 'danger'}
                />
                {rowData.isDefault && <Badge value={t('templateManager.status.default')} severity="info" />}
            </div>
        );
    };

    usageBodyTemplate = (rowData) => {
        const { t } = this.props;
        return (
            <div className="text-center">
                <div className="font-bold text-lg">{rowData.usageCount}</div>
                <div className="text-sm text-muted">{t('templateManager.messages.usages')}</div>
            </div>
        );
    };

    actionsBodyTemplate = (rowData) => {
        const { t } = this.props;
        const canEdit = rowData.id > 0; // Only allow editing templates with positive IDs

        const items = [
            {
                label: t('templateManager.actions.duplicate'),
                icon: 'pi pi-copy',
                command: () => this.openDialog('duplicate', rowData)
            },
            {
                label: t('templateManager.actions.export'),
                icon: 'pi pi-download',
                command: () => this.exportTemplate(rowData)
            },
            {
                separator: true
            },
            {
                label: t('templateManager.actions.delete'),
                icon: 'pi pi-trash',
                command: () => this.deleteTemplate(rowData),
                disabled: !canEdit
            }
        ];

        return (
            <div className="flex gap-2">
                <Button
                    icon="pi pi-eye"
                    className="p-button-rounded p-button-text"
                    onClick={() => this.setState({ selectedTemplate: rowData, showPreviewDialog: true })}
                    tooltip={t('templateManager.actions.preview')}
                />
                <Button
                    icon="pi pi-pencil"
                    className="p-button-rounded p-button-text"
                    onClick={() => this.openDialog('edit', rowData)}
                    tooltip={t('templateManager.actions.edit')}
                    disabled={!canEdit}
                />
                <Button
                    icon="pi pi-palette"
                    className="p-button-rounded p-button-text p-button-success"
                    onClick={() => this.openTemplateEditor(rowData)}
                    tooltip={t('templateManager.buttons.graphicEditor')}
                    disabled={!canEdit}
                />
                <SplitButton
                    icon="pi pi-ellipsis-v"
                    className="p-button-rounded p-button-text"
                    model={items}
                    tooltip={t('templateManager.actions.moreActions')}
                />
            </div>
        );
    };

    renderHeader = () => {
        const { t } = this.props;
        const { countriesLoading } = this.state;
        const countryOptions = this.getCountryOptions();
        return (
            <div className="flex justify-content-between align-items-center">
                <div className="flex gap-2">
                    <span className="p-input-icon-left">
                        <i className="pi pi-search" />
                        <InputText
                            type="search"
                            onInput={(e) => this.setState({ globalFilter: e.target.value })}
                            placeholder={t('templateManager.search')}
                        />
                    </span>
                    <Dropdown
                        value={this.state.filters.country}
                        options={[{ label: t('templateManager.filters.allCountries'), value: null }, ...countryOptions]}
                        onChange={(e) => this.setState(prev => ({ filters: { ...prev.filters, country: e.value } }))}
                        placeholder={t('templateManager.filters.country')}
                        showClear
                        disabled={countriesLoading}
                        emptyMessage={      countriesLoading ? t('common.loading') : t('templateManager.filters.noCountryAvailable')}
                    />
                    <Dropdown
                        value={this.state.filters.type}
                        options={[{ label: t('templateManager.filters.allTypes'), value: null }, ...this.getTypes()]}
                        onChange={(e) => this.setState(prev => ({ filters: { ...prev.filters, type: e.value } }))}
                        placeholder={t('templateManager.filters.type')}
                        showClear
                    />
                </div>
                <div className="flex gap-2">
                    <FileUpload
                        mode="basic"
                        accept=".json"
                        maxFileSize={1000000}
                        customUpload
                        uploadHandler={this.importTemplate}
                        chooseLabel={t('templateManager.buttons.import')}
                        className="p-button-outlined"
                    />
                    <Button
                        label={t('templateManager.buttons.newTemplate')}
                        icon="pi pi-plus"
                        onClick={() => this.openDialog('create')}
                    />
                    <Button
                        label={t('templateManager.buttons.graphicEditor')}
                        icon="pi pi-palette"
                        className="p-button-success"
                        onClick={() => this.openTemplateEditor()}
                    />
                </div>
            </div>
        );
    };

    renderDialog = () => {
        const { t } = this.props;
        const { showDialog, dialogMode, templateForm, countriesLoading } = this.state;
        const countryOptions = this.getCountryOptions();

        const dialogHeader = dialogMode === 'create' ? t('templateManager.dialog.newTemplate') :
                            dialogMode === 'edit' ? t('templateManager.dialog.editTemplate') : t('templateManager.dialog.duplicateTemplate');

        return (
            <Dialog
                visible={showDialog}
                style={{ width: '800px' }}
                header={dialogHeader}
                modal
                onHide={() => this.setState({ showDialog: false })}
                footer={
                    <div>
                        <Button
                            label={t('templateManager.buttons.cancel')}
                            icon="pi pi-times"
                            className="p-button-text"
                            onClick={() => this.setState({ showDialog: false })}
                        />
                        <Button
                            label={t('templateManager.buttons.save')}
                            icon="pi pi-check"
                            onClick={this.saveTemplate}
                            loading={this.state.loading}
                        />
                    </div>
                }
            >
                <div className="p-fluid">
                    <Row>
                        <Col md={12}>
                            <div className="field">
                                <label htmlFor="name">{t('templateManager.dialog.templateName')} *</label>
                                <InputText
                                    id="name"
                                    value={templateForm.name}
                                    onChange={(e) => this.setState(prev => ({
                                        templateForm: { ...prev.templateForm, name: e.target.value }
                                    }))}
                                    placeholder={t('templateManager.dialog.enterTemplateName')}
                                />
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12}>
                            <div className="field">
                                <label htmlFor="country">{t('templateManager.dialog.country')} *</label>
                                <Dropdown
                                    id="country"
                                    value={templateForm.country}
                                    options={countryOptions}
                                    onChange={(e) => this.setState(prev => ({
                                        templateForm: { ...prev.templateForm, country: e.value }
                                    }))}
                                    placeholder={t('templateManager.dialog.selectCountry')}
                                    disabled={countriesLoading} // ✅ Désactivé pendant chargement
                                    emptyMessage={countriesLoading ? t('common.loading') : t('templateManager.dialog.noCountryAvailable')}
                                />
                                {countriesLoading && (
                                    <small className="text-muted d-block mt-2">
                                        {t('common.loading')}...
                                    </small>
                                )}
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <div className="field-checkbox">
                                <Checkbox
                                    id="isActive"
                                    checked={templateForm.isActive}
                                    onChange={(e) => this.setState(prev => ({
                                        templateForm: { ...prev.templateForm, isActive: e.checked }
                                    }))}
                                />
                                <label htmlFor="isActive">{t('templateManager.dialog.isActive')}</label>
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="field-checkbox">
                                <Checkbox
                                    id="isDefault"
                                    checked={templateForm.isDefault}
                                    onChange={(e) => this.setState(prev => ({
                                        templateForm: { ...prev.templateForm, isDefault: e.checked }
                                    }))}
                                />
                                <label htmlFor="isDefault">{t('templateManager.dialog.isDefault')}</label>
                            </div>
                        </Col>
                    </Row>
                </div>
            </Dialog>
        );
    };

    render() {
        const { t } = this.props;
        const { templates, loading, globalFilter, selectedTemplates } = this.state;

        return (
            <div className="template-manager">
                <Toast ref={this.toast} />
                <ConfirmDialog />

                <Card>
                    <Toolbar
                        className="mb-4"
                        left={this.renderHeader()}
                    />

                    <DataTable
                        value={templates}
                        loading={loading}
                        dataKey="id"
                        paginator
                        rows={10}
                        rowsPerPageOptions={[5, 10, 25]}
                        globalFilter={globalFilter}
                        emptyMessage={t('templateManager.messages.noTemplateFound')}
                        responsiveLayout="scroll"
                        sortField="createdAt"
                        sortOrder={-1}
                    >
                        <Column
                            field="name"
                            header={t('templateManager.columns.name')}
                            body={this.nameBodyTemplate}
                            sortable
                        />
                        <Column
                            field="isActive"
                            header={t('templateManager.columns.status')}
                            body={this.statusBodyTemplate}
                            sortable
                        />
                        <Column
                            field="usageCount"
                            header={t('templateManager.columns.usage')}
                            body={this.usageBodyTemplate}
                            sortable
                        />
                        <Column
                            field="createdAt"
                            header={t('templateManager.columns.createdAt')}
                            body={(rowData) => new Date(rowData.createdAt).toLocaleDateString()}
                            sortable
                        />
                        <Column
                            field="updatedAt"
                            header={t('templateManager.columns.updatedAt')}
                            body={(rowData) => new Date(rowData.updatedAt).toLocaleDateString()}
                            sortable
                        />
                        <Column
                            body={this.actionsBodyTemplate}
                            header={t('templateManager.columns.actions')}
                            style={{ width: '150px' }}
                        />
                    </DataTable>
                </Card>

                {this.renderDialog()}

                {/* Dialogs pour prévisualisation, comparaison et éditeur */}
                <TemplatePreview
                    visible={this.state.showPreviewDialog}
                    template={this.state.selectedTemplate}
                    onHide={() => this.setState({ showPreviewDialog: false })}
                />

                <TemplateComparison
                    visible={this.state.showComparisonDialog}
                    templates={selectedTemplates}
                    onHide={() => this.setState({ showComparisonDialog: false })}
                />

                <TemplateEditor
                    visible={this.state.showEditorDialog}
                    template={this.state.selectedTemplate}
                    onHide={() => this.setState({ showEditorDialog: false })}
                    onSave={this.saveTemplate}
                />
            </div>
        );
    }
}

export default withRouter(withTranslation()(TemplateManager));